import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { sa } from '../superAdminUi';

// Reusable controlled tab container for the account views and the dashboard home. Each tab is
// `{ key, label, content }`; the first tab is shown by default unless `defaultTabKey` names
// another. Rendered as the Figma pill tab bar (white bordered track, teal active pill).
// Selection lives in local state so the component stays router-independent and testable.

const TabBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;  
`;

const TabPill = styled.button`
  border: none;
  cursor: pointer;
  font-family: ${sa.font.family};
  font-weight: ${sa.font.semibold};
  font-size: 14px;
  line-height: normal;
  padding: 12px 18px;
  border-radius: ${sa.radius.sm};
  background: ${(p) => (p.$active ? sa.color.teal : sa.color.tabInactive)};
  color: ${(p) => (p.$active ? sa.color.white : sa.color.text)};
  transition: background-color 0.15s ease-in-out;
`;

const Panel = styled.div`
  margin-top: ${sa.space.section};
`;

function AccountTabs({ tabs, defaultTabKey, idBase }) {
  const firstKey = tabs.length > 0 ? tabs[0].key : undefined;
  const hasDefault = tabs.some((tab) => tab.key === defaultTabKey);
  const [activeKey, setActiveKey] = useState(hasDefault ? defaultTabKey : firstKey);

  return (
    <div style={{ width: '100%', marginTop: sa.space.section }}>
      <TabBar role='tablist' aria-label='account view tabs'>
        {tabs.map((tab) => {
          const active = tab.key === activeKey;
          return (
            <TabPill
              key={tab.key}
              type='button'
              role='tab'
              $active={active}
              aria-selected={active}
              id={`${idBase}-tab-${tab.key}`}
              aria-controls={`${idBase}-panel-${tab.key}`}
              onClick={() => setActiveKey(tab.key)}
            >
              {tab.label}
            </TabPill>
          );
        })}
      </TabBar>

      {tabs.map((tab) => {
        const isSelected = tab.key === activeKey;
        return (
          <div
            key={tab.key}
            role='tabpanel'
            hidden={!isSelected}
            id={`${idBase}-panel-${tab.key}`}
            aria-labelledby={`${idBase}-tab-${tab.key}`}
          >
            {isSelected && <Panel>{tab.content}</Panel>}
          </div>
        );
      })}
    </div>
  );
}

AccountTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      content: PropTypes.node,
    })
  ).isRequired,
  defaultTabKey: PropTypes.string,
  idBase: PropTypes.string,
};

AccountTabs.defaultProps = {
  idBase: 'account',
};

export default AccountTabs;
